import React, {useEffect, useId} from 'react'
import {useLocation} from 'react-router-dom'
import PropTypes from 'prop-types'
import {Box} from '@chakra-ui/react'
import {useProducts} from '@salesforce/commerce-sdk-react'
import PDCTitle from '@app/components/pdc-title'
import usePDCResponsiveElement from '@app/hooks/use-pdc-responsive-element.js'
import {ProductCarousel} from '@app/components/product-carousel/index.jsx'

// Custom Hooks
import {useGTM} from '@app/hooks/use-gtm'

/**
 * PDCClassicProductCarousel is a React functional component that renders a product carousel
 * with customizable display options for product images, names, prices, and color variations.
 * It adapts the number of visible slides based on the screen size using responsive design principles.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.title - The title of the product carousel.
 * @param {string} [props.titleHeadingLevel='h2'] - The heading level for the title, defaulting to 'h2'.
 * @param {string} [props.titleFontColor='fullBlack'] - The font color of the title, defaulting to 'fullBlack'.
 * @param {number} props.smallNbSlidesToShow - Number of slides to show on small screens.
 * @param {number} props.mediumNbSlidesToShow - Number of slides to show on medium screens.
 * @param {number} props.largeNbSlidesToShow - Number of slides to show on large screens.
 * @param {number} props.xlargeNbSlidesToShow - Number of slides to show on extra-large screens.
 * @param {number} props.xxlargeNbSlidesToShow - Number of slides to show on extra-extra-large screens.
 * @param {boolean} props.showProductImages - Flag to display product images.
 * @param {boolean} props.showProductNames - Flag to display product names.
 * @param {boolean} props.showProductPrices - Flag to display product prices.
 * @param {boolean} props.showProductColorVariations - Flag to display product color variations.
 * @param {Array} props.regions - Array of region objects containing components to be displayed in the carousel.
 *
 * @returns {JSX.Element} A section element containing the product carousel.
 */
export const PDCClassicProductCarousel = ({
    title,
    titleHeadingLevel = 'h2',
    titleFontColor = 'fullBlack',
    smallNbSlidesToShow,
    mediumNbSlidesToShow,
    largeNbSlidesToShow,
    xlargeNbSlidesToShow,
    xxlargeNbSlidesToShow,
    showProductImages,
    showProductNames,
    showProductPrices,
    showProductColorVariations,
    isSlider,
    regions
}) => {
    const {triggerViewItemListEvent} = useGTM()
    const location = useLocation()
    const isHomePage = location.pathname.includes('/home')

    // Get the tiles components from the regions
    let tiles = []
    if (regions) {
        regions.forEach((region) => {
            if (region.id === 'pdc_classic_product_carousel_tiles') {
                tiles = region.components
            }
        })
    }

    // Get products
    const productIds = tiles.map((tile) => tile?.data?.product)
    const {data: products, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: productIds?.join(','),
                expand: ['promotions', 'prices', 'images'],
                c_isHomePage: isHomePage
            }
        },
        {enabled: productIds?.length > 0}
    )

    useEffect(() => {
        if (!isProductsLoading) {
            triggerViewItemListEvent(products?.data, 'homepage')
        }
    }, [isProductsLoading])

    // Get the number of slides to show depending on the screen size
    const nbSlidesToShowByBreakpoint = usePDCResponsiveElement({
        smallEl: smallNbSlidesToShow || 1,
        mediumEl: mediumNbSlidesToShow || 2,
        largeEl: largeNbSlidesToShow,
        xlargeEl: xlargeNbSlidesToShow,
        xxlargeEl: xxlargeNbSlidesToShow
    })

    const titleId = useId()

    return (
        <Box as="section" aria-describedby={titleId} css={[PDCClassicProductCarousel.css]}>
            <ProductCarousel
                title={
                    <PDCTitle
                        width={'100%'}
                        title={title}
                        titleFontColor={titleFontColor}
                        titleHeadingLevel={titleHeadingLevel}
                        txtHorizontalPos={'left'}
                        id={titleId}
                        size="4xl"
                    />
                }
                nbSlidesToShow={nbSlidesToShowByBreakpoint}
                showProductImages={showProductImages}
                showProductNames={showProductNames}
                showProductPrices={showProductPrices}
                showProductColorVariations={showProductColorVariations}
                products={products?.data}
                isLoading={isProductsLoading}
                isSwitchedImages={true}
                isSlider={isSlider}
                pt={['80px', null, '120px']}
            />
        </Box>
    )
}

PDCClassicProductCarousel.propTypes = {
    title: PropTypes.string.isRequired,
    titleHeadingLevel: PropTypes.string,
    titleFontColor: PropTypes.string,
    smallNbSlidesToShow: PropTypes.number,
    mediumNbSlidesToShow: PropTypes.number,
    largeNbSlidesToShow: PropTypes.number,
    xlargeNbSlidesToShow: PropTypes.number,
    xxlargeNbSlidesToShow: PropTypes.number,
    showProductImages: PropTypes.bool,
    showProductNames: PropTypes.bool,
    showProductPrices: PropTypes.bool,
    showProductColorVariations: PropTypes.bool,
    isSlider: PropTypes.bool,
    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    data: PropTypes.object,
                    id: PropTypes.string,
                    typeId: PropTypes.string
                })
            )
        })
    )
}
